import { ft } from '../core/units.js';
import { EVENT } from './events.js';
import { INDIVIDUALS } from '../data/individuals.js';

/**
 * One animal, that you meet more than once, and that remembers.
 *
 * The only memory any animal in the game has, and it is exactly one thing:
 * the first time you met it in the golf cart, did you come at it or go round
 * it. Every meeting after that is decided by that, and the screen says so.
 *
 *   coming at it   the cart's line would have run over it. It moves off the
 *                  line, every time, faster than a cart; nothing ever touches
 *                  it and nothing can kill it. What is kept is that you tried
 *   going round    you passed without that line
 *
 *   afterwards     came at it: it strikes at you, through the impact bus like
 *                  a javelina arriving. Went round: it lets you by
 *
 * Saved with the week, and only this. Where it is lying is its place.
 */

/** Metres per second it gets off the line at. Quicker than any cart. */
const SLIDE = 7;

/** Metres of clearance past the cart's edge it always ends up at. */
const CLEAR = 0.5;

export function createIndividuals({ bus, rows = INDIVIDUALS }) {
  const fresh = (row) => ({
    position: { x: ft(row.lies[0]), y: 0, z: ft(row.lies[1]) },
    verdict: null,
    meetings: 0,
    meeting: false,
    cameAt: false,
  });
  let state = new Map(rows.map((row) => [row.id, fresh(row)]));

  function update(dt, { vehicle = null, radius = 0 } = {}) {
    const said = [];
    for (const row of rows) {
      const me = state.get(row.id);
      const home = { x: ft(row.lies[0]), z: ft(row.lies[1]) };
      if (vehicle) {
        const dx = me.position.x - vehicle.position.x;
        const dz = me.position.z - vehicle.position.z;
        const gap = Math.hypot(dx, dz);

        if (!me.meeting && gap < ft(row.meets)) {
          me.meeting = true;
          me.meetings += 1;
          // The second time, and every time after: what you did the first.
          if (me.verdict === 'aimed') {
            bus.emit(EVENT.NOISE, { actor: 'wildlife', cause: 'rattle', kind: row.kind, position: { ...me.position }, radius: ft(20) });
            bus.emit(EVENT.IMPACT, {
              actor: 'wildlife',
              kind: row.kind,
              target: { type: 'body', id: 'player' },
              position: { ...me.position },
              energy: 12,
              speed: 3,
              broke: false,
              individual: row.id,
            });
            said.push({ id: row.id, struck: true, line: row.says.strikes });
          } else if (me.verdict === 'around') {
            said.push({ id: row.id, struck: false, line: row.says.lets });
          }
        }

        if (me.meeting) {
          const speed = Math.hypot(vehicle.velocity.x, vehicle.velocity.z);
          const fx = Math.sin(vehicle.yaw);
          const fz = Math.cos(vehicle.yaw);
          const along = dx * fx + dz * fz;
          const across = dx * fz - dz * fx;
          const reach = radius + ft(row.aim);
          // On the cart's line and ahead of it: that is coming at it.
          if (along > 0 && Math.abs(across) < reach && speed > 0.5) {
            if (me.verdict === null) me.cameAt = true;
            // And it goes, off the line, whichever side it is already on.
            const side = across >= 0 ? 1 : -1;
            const want = radius + CLEAR + ft(row.aim);
            const step = Math.min(SLIDE * dt, want - Math.abs(across));
            if (step > 0) {
              me.position.x += fz * side * step;
              me.position.z += -fx * side * step;
            }
          }
          // And never under it, whatever the cart did in the frame.
          const nx = me.position.x - vehicle.position.x;
          const nz = me.position.z - vehicle.position.z;
          const now = Math.hypot(nx, nz);
          if (now < radius + CLEAR) {
            const out = (radius + CLEAR) / Math.max(now, 1e-4);
            me.position.x = vehicle.position.x + (now > 1e-4 ? nx : fz) * out;
            me.position.z = vehicle.position.z + (now > 1e-4 ? nz : -fx) * out;
          }
          if (gap > ft(row.leaves)) {
            me.meeting = false;
            if (me.verdict === null) me.verdict = me.cameAt ? 'aimed' : 'around';
          }
        }
      }
      // Back to its place, slowly, once nothing is near it.
      if (!me.meeting) {
        const hx = home.x - me.position.x;
        const hz = home.z - me.position.z;
        const back = Math.hypot(hx, hz);
        if (back > 0.05) {
          const step = Math.min(0.3 * dt, back);
          me.position.x += (hx / back) * step;
          me.position.z += (hz / back) * step;
        }
      }
    }
    return said;
  }

  return {
    rows,
    update,

    /** Where each one is and what it remembers, for the view and a check. */
    get all() {
      return rows.map((row) => ({ id: row.id, kind: row.kind, ...state.get(row.id), position: { ...state.get(row.id).position } }));
    },

    toJSON: () =>
      Object.fromEntries(rows.map((row) => [row.id, { verdict: state.get(row.id).verdict, meetings: state.get(row.id).meetings }])),

    load(saved) {
      state = new Map(rows.map((row) => [row.id, { ...fresh(row), ...(saved?.[row.id] ?? {}) }]));
    },

    clear() {
      state = new Map(rows.map((row) => [row.id, fresh(row)]));
    },
  };
}
